import type { WorldState } from '../types/Components';
import type { Entity } from '../types/Entity';
import type { Intention } from '../types/AI';
import { isIntentionProvider } from '../types/Components';
import entityManager from '../lib/entityManager';
import { WantsToAct, HexPosition, PlayerControlled, IntendsToAct, Idle, IsWalkable, ActorStats, PathfindingAI } from '../components';
import { ActionType } from '../lib/Action';

export default class AISystem {
  update(): void {
    // 1. Query for entities that want to act and are not the player.
    const actingEntities = entityManager.query([WantsToAct, ActorStats, HexPosition]);
    const aiEntities = actingEntities.filter(e => !e.hasComponent(PlayerControlled));

    if (aiEntities.length === 0) {
      return; // No AI needs to act.
    }

    // 2. Gather world state needed by intention-giving components.
    const playerEntities = entityManager.query([PlayerControlled, HexPosition]);
    const player = playerEntities.length > 0 ? playerEntities[0] : null;
    const playerPosition = player ? (player.getComponent(HexPosition) || null) : null;

    const walkableCoords = new Set<string>();
    entityManager.query([IsWalkable, HexPosition]).forEach((e: Entity) => {
      walkableCoords.add(e.getComponent(HexPosition)!.coords.key);
    });

    const occupiedCoords = new Map<string, Entity>();
    entityManager.query([ActorStats, HexPosition]).forEach((e: Entity) => {
      occupiedCoords.set(e.getComponent(HexPosition)!.coords.key, e);
    });

    const worldState: WorldState = {
      player,
      playerPosition,
      walkableCoords,
      occupiedCoords
    };

    // 3. For each AI, gather and prioritize intentions.
    aiEntities.forEach((entity: Entity) => {
      // If there's no player, AI's can't target, so they will default to Idle.
      if (!player) {
        if (entity.hasComponent(Idle)) {
          entity.addComponent(IntendsToAct, new IntendsToAct({ actionType: ActionType.IDLE, details: {} }));
        }
        entity.removeComponent(WantsToAct);
        return;
      }

      const intentions: Intention[] = [];
      // Iterate over all components of the entity
      for (const component of entity.getAllComponents()) {
        if (isIntentionProvider(component)) {
          // Pass world state to get the component's intention
          const intention = component.getIntention(entity, worldState);
          if (intention) {
            intentions.push(intention);
          }
        }
      }

      let bestIntention: Intention | null = null;
      if (intentions.length > 0) {
        // Find the intention with the highest priority
        bestIntention = intentions.reduce((best, current) => {
          return (!best || current.priority > best.priority) ? current : best;
        }, null as Intention | null);
      }

      if (bestIntention) {
        const details = { ...bestIntention.details };
        if (details.target) {
          details.targetId = details.target.id;
          delete details.target;
        }
        entity.addComponent(IntendsToAct, new IntendsToAct({ actionType: bestIntention.type, details, sourceComponentName: bestIntention.sourceComponentName }));
      } else {
        // If no component provides an intention, default to Idle.
        entity.addComponent(IntendsToAct, new IntendsToAct({ actionType: ActionType.IDLE, details: {}, sourceComponentName: 'Idle' }));
      }

      entity.removeComponent(WantsToAct);
    });
  }
}
